// src/features/hmi-simulator/components/HMISimulator.tsx
import React, { useState, useEffect } from 'react';
import { Box, CircularProgress } from '@mui/material';
import { useTranslate } from 'react-admin';
import { useHMIApi } from '../hooks/useHMIApi';
import { useHeartbeat } from '../hooks/useHeartbeat';
import { DEFAULT_CONFIG } from '../constants/config';
import { HMIConfig, LockerInfo } from '../types';

// Importar pantallas
import { ProvisionScreen } from './screens/ProvisionScreen';
import { MainScreen } from './screens/MainScreen';
import { LockerScreen } from './screens/LockerScreen';
import { StaffScreen } from './screens/StaffScreen';
import { SettingsScreen } from './screens/SettingsScreen';

type ScreenType = 'provision' | 'main' | 'locker' | 'staff' | 'settings';

const HMISimulator: React.FC = () => {
  const { execute, api, loading: apiLoading } = useHMIApi();
  const translate = useTranslate();
  
  // ========== ESTADOS PRINCIPALES ==========
  const [screen, setScreen] = useState<ScreenType>('provision');
  const [isProvisioned, setIsProvisioned] = useState(false);
  const [config, setConfig] = useState<HMIConfig>(DEFAULT_CONFIG);
  const [controllerConfig, setControllerConfig] = useState<any>(null);
  const [language, setLanguage] = useState('EN');
  const [isOnline, setIsOnline] = useState(false);
  const [loading, setLoading] = useState(false);
  
  // ========== ESTADOS DE PROVISIONING ==========
  const [provisioningCode, setProvisioningCode] = useState('');
  const [provisioningError, setProvisioningError] = useState('');
  
  // ========== ESTADOS DE OPERACIÓN PRINCIPAL ==========
  const [email, setEmail] = useState('');
  const [accessCode, setAccessCode] = useState('');
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  
  // ========== ESTADOS DE LOCKER ==========
  const [currentLocker, setCurrentLocker] = useState<LockerInfo | null>(null);
  const [lockerAction, setLockerAction] = useState<'confirm' | 'checkout' | null>(null);
  
  // ========== ESTADOS DE STAFF ==========
  const [staffPassword, setStaffPassword] = useState('');
  const [staffSession, setStaffSession] = useState<string | null>(null);
  const [lockerStatuses, setLockerStatuses] = useState<Array<{lockerName: string; lockerAvailable: boolean}>>([]);
  const [selectedLocker, setSelectedLocker] = useState('');
  const [staffError, setStaffError] = useState('');
  
  // ========== ESTADOS DE CONTROLADORES ==========
  const [controllerStatuses, setControllerStatuses] = useState<Map<string, boolean>>(new Map());

  // ========== CUSTOM HOOK PARA HEARTBEAT ==========
  useHeartbeat(isProvisioned, controllerConfig, api, setIsOnline);
  
  // ========== EFECTOS ==========
  // Verificar si ya hay credenciales guardadas al cargar
  useEffect(() => {
    const credentials = api.getCredentials();
    if (credentials) {
      setIsProvisioned(true);
      setScreen('main');
      loadConfiguration();
    }
  }, []);
  
// Fragmento actualizado de loadConfiguration en HMISimulator.tsx

const loadConfiguration = async () => {
  setLoading(true);
  
  try {
    // Obtener configuración general
    const configData = await execute(() => api.getConfig());
    
    if (configData) {
      const transformedConfig: HMIConfig = {
        hotelName: configData.hotel_name || 'Hotel',
        primaryColor: configData.primary_color || '#007bff',
        availableLanguages: configData.available_languages || ['EN'],
        defaultLanguage: configData.default_language || 'EN',
        logoBase64: configData.logo_base64 || '',
        texts: configData.texts || DEFAULT_CONFIG.texts
      };
      
      setConfig(transformedConfig);
      setLanguage(transformedConfig.defaultLanguage);
    }
    
    // Obtener configuración de controladores
    const controllerData = await execute(() => api.getControllerConfig());
    
    if (controllerData) {
      console.log('Controller data received:', controllerData);
      
      // El backend devuelve camelCase, así que lo guardamos tal cual
      // Solo nos aseguramos de que los arrays estén disponibles
      setControllerConfig(controllerData);
      
      // Inicializar estado de controladores
      const initialStatuses = new Map<string, boolean>();
      const lockerControllers = controllerData.lockerControllers || [];
      
      lockerControllers.forEach((controller: any) => {
        const key = `${controller.ipAddress}:${controller.tcpPort}`;
        initialStatuses.set(key, true);
      });
      
      setControllerStatuses(initialStatuses);
      
      console.log('Controller config set:', controllerData);
      console.log('Initial controller statuses:', Array.from(initialStatuses.entries()));
    }
  } catch (err) {
    console.error('Error loading configuration:', err);
  } finally {
    setLoading(false);
  }
};
  
  // ========== FUNCIONES HELPER ==========
  const t = (key: string): string => {
    return config.texts[language]?.[key] || 
           config.texts[config.defaultLanguage]?.[key] || 
           config.texts['EN']?.[key] || 
           key.replace(/_/g, ' ');
  };
  
  // ========== HANDLERS DE PROVISIONING ==========
  const handleProvisioning = async () => {
    setProvisioningError('');
    const result = await execute(
      () => api.provision(provisioningCode),
      {
        successMessage: translate('hmiSimulator.messages.device_provisioned_successfully'),
        showError: false
      }
    );
    
    if (result) {
      setIsProvisioned(true);
      setIsOnline(true);
      await loadConfiguration();
      setScreen('main');
    } else {
      setProvisioningError('Código de provisioning inválido o expirado');
    }
  };
  
  // ========== HANDLERS DE EMAIL ==========
  const handleEmailSubmit = async () => {
    setError('');
    setMessage('');
    const result = await execute(
      () => api.createEmailAssignment(email),
      { showError: false }
    );
    
    if (result) {
      setEmail('');
      setMessage(result.message?.[language] || result.message?.['EN'] || 'Email enviado');
    } else {
      setError('Error al enviar el email');
    }
  };
  
  // ========== HANDLERS DE CÓDIGO ==========
  const handleCodeSubmit = async () => {
    setError('');
    const result = await execute(
      () => api.validateCode(accessCode),
      { showError: false }
    );
    
    if (result) {
      setCurrentLocker({
        lockerId: result.locker_id,
        lockerName: result.locker_name,
        action: result.action,
        ipAddress: result.ip_address,
        tcpPort: result.tcp_port,
      });
      setScreen('locker');
    } else {
      setError('Código inválido o expirado');
    }
  };
  
  // ========== HANDLERS DE LOCKER ==========
  const handleLockerConfirm = async () => {
    setError('');
    const result = await execute(
      () => api.confirmLocker(currentLocker?.lockerName || '', accessCode),
      { showError: false }
    );
    
    if (result) {
      setLockerAction('confirm');
      setTimeout(() => {
        setLockerAction('checkout');
      }, 2000);
    } else {
      setError('Error al confirmar el locker');
    }
  };

  const handleLockerCheckout = async () => {
    const result = await execute(
      () => api.checkoutLocker(currentLocker?.lockerName || '', accessCode),
      { showError: false }
    );
    
    if (result) {
      setLockerAction('checkout');
      setTimeout(() => {
        setScreen('main');
        setCurrentLocker(null);
        setLockerAction(null);
        setAccessCode('');
      }, 3000);
    }
  };

  const handleLockerCancel = () => {
    setScreen('main');
    setCurrentLocker(null);
    setAccessCode('');
    setLockerAction(null);
  };
  
  // ========== HANDLERS DE STAFF ==========
  const handleStaffLogin = async () => {
    setStaffError('');
    const result = await execute(
      () => api.staffLogin(staffPassword),
      { showError: false }
    );
    
    if (result) {
      setStaffSession(result.session_token);
      setStaffPassword('');
      await handleRefreshLockers(result.session_token);
    } else {
      setStaffError('Invalid password');
    }
  };

  const handleRefreshLockers = async (sessionToken?: string) => {
    const token = sessionToken || staffSession;
    if (!token) return;
    
    const statuses = await execute(
      () => api.getLockersStatus(token),
      { showError: false }
    );
    
    if (statuses) {
      setLockerStatuses(statuses);
    }
  };

  const handleStaffOpenLocker = async () => {
    if (!selectedLocker || !staffSession) return;
    
    setMessage('');
    const result = await execute(
      () => api.staffOpenLocker(selectedLocker, staffSession),
      { showError: false }
    );
    
    if (result) {
      setMessage(`Locker ${selectedLocker} opened successfully`);
      await handleRefreshLockers();
      
      setTimeout(() => {
        setSelectedLocker('');
        setMessage('');
      }, 2000);
    }
  };

  const handleStaffLogout = () => {
    setStaffSession(null);
    setLockerStatuses([]);
    setSelectedLocker('');
    setStaffPassword('');
  };
  
  // ========== HANDLERS DE SETTINGS ==========
  const handleResetDevice = () => {
    api.clearCredentials();
    setIsProvisioned(false);
    setScreen('provision');
    setControllerConfig(null);
    setControllerStatuses(new Map());
    // Reset todos los estados
    setProvisioningCode('');
    setEmail('');
    setAccessCode('');
    setCurrentLocker(null);
    setStaffSession(null);
    setSelectedLocker('');
  };
  
  // ========== RENDERIZADO ==========
  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh' }}>
        <CircularProgress />
      </Box>
    );
  }
  
  // Props comunes para todas las pantallas
  const commonProps = {
    config,
    language,
    setLanguage,
    isOnline,
    apiLoading,
    t,
    api,
    setScreen,
  };
  
  // Renderizado condicional por pantalla
    switch (screen) {
    case 'provision':
      return (
        <ProvisionScreen
          provisioningCode={provisioningCode}
          setProvisioningCode={setProvisioningCode}
          error={provisioningError}
          apiLoading={apiLoading}
          onProvision={handleProvisioning}
        />
      );
      
    case 'main':
      return (
        <MainScreen
          {...commonProps}
          email={email}
          setEmail={setEmail}
          accessCode={accessCode}
          setAccessCode={setAccessCode}
          message={message}
          error={error}
          onEmailSubmit={handleEmailSubmit}
          onCodeSubmit={handleCodeSubmit}
        />
      );
      
    case 'locker':
      return (
        <LockerScreen
          {...commonProps}
          currentLocker={currentLocker}
          lockerAction={lockerAction}
          error={error}
          onConfirm={handleLockerConfirm}
          onCheckout={handleLockerCheckout}
          onCancel={handleLockerCancel}
        />
      );
      
    case 'staff':
      return (
        <StaffScreen
          config={config}
          staffSession={staffSession}
          staffPassword={staffPassword}
          setStaffPassword={setStaffPassword}
          staffError={staffError}
          lockerStatuses={lockerStatuses}
          selectedLocker={selectedLocker}
          setSelectedLocker={setSelectedLocker}
          message={message}
          apiLoading={apiLoading}
          handleStaffLogin={handleStaffLogin}
          handleRefreshLockers={handleRefreshLockers}
          handleStaffOpenLocker={handleStaffOpenLocker}
          setStaffSession={setStaffSession}
          setScreen={setScreen}
        />
      );
      
    case 'settings':
      return (
        <SettingsScreen
          {...commonProps}
          controllerConfig={controllerConfig}
          controllerStatuses={controllerStatuses}
          deviceId={api.getCredentials()?.deviceId}
          onResetDevice={handleResetDevice}
        />
      );
      
    default:
      return (
        <ProvisionScreen
          provisioningCode={provisioningCode}
          setProvisioningCode={setProvisioningCode}
          error={provisioningError}
          apiLoading={apiLoading}
          onProvision={handleProvisioning}
        />
      );
  }
};

export default HMISimulator;